<template lang="pug">
form#event-form.mobile-padding(
  @submit.prevent="submit"
  name="event-form"
  novalidate
)
  .event-form-block(
    v-for="block in form.blocks"
    :key="block.id"
  )
    h3.is-size-5 {{ block.heading }}
    .event-form-block-paragraph(v-if="block.paragraph")
      p {{ block.paragraph }}
    div(v-if="block.type === 'custom'")
      event-form-field(
        v-for="field in block.form_fields"
        data-vv-delay="1000"
        v-validate="field.validations"
        :key="field.id"
        :name="field.name"
        :value="field"
        :data-vv-as="field.lowercase_label"
        :type="errors.has(field.name) ? 'is-danger' : ''"
        :message="errors.has(field.name) ? errors.first(field.name) : ''"
        @input="setField($event, field)"
      )
    div(v-else-if="block.type === 'participant'")
      .participant(
        v-for="(participant, $index) in participants"
        :key="$index"
      )
        b-field(grouped)
          event-form-field(
            v-for="field in block.form_fields"
            data-vv-delay="1000"
            v-validate="field.validations"
            :name="field.name"
            :key="field.id"
            :expanded="field.type === 'text'"
            :value="field"
            :data-vv-as="field.lowercase_label"
            :type="errors.has(field.name) ? 'is-danger' : ''"
            :message="errors.has(field.name) ? errors.first(field.name) : ''"
            @input="setParticipantField($event, field, $index)"
          )
          b-field.participant-actions(label="Actions")
            p.control
              b-tooltip(label="Remove this person" position="is-top" type="is-dark")
                a.button.is-text(:disabled="$index === 0" @click="removeParticipant($index)")
                  b-icon(icon="close-circle-outline")
      a.button.is-light(@click="addParticipant")
        b-icon(icon="plus")
        span Add another person
    div(v-else-if="block.type === 'payment' && amount > 0")
      .credit-cards
        cc-icon(brand="Visa" :width="46")
        cc-icon(brand="Mastercard" :width="46")
        cc-icon(brand="Discover" :width="46")
        cc-icon(brand="American Express" :width="46")
      .event-packages(v-if="event._embedded.packages.length > 1")
        h3.is-size-5.package-heading Choose a package:
        b-field(
          v-for="pack in event._embedded.packages"
          :key="pack.id"
        )
          b-radio(
            name="package_id"
            :value="selectedPackage.id"
            :native-value="pack.id"
            @input="selectPackage(pack)"
          )
            span.is-size-6
              b ${{ pack.cost }}
              span(style="padding-left:0.25rem;") {{ pack.name }}

        .payment-total
          p.is-size-5 Order Total
          h3.is-size-2 {{ amount | cents }}
      b-field.payment-fields(grouped)
        b-field.cc-field.cc-number(for="card-element" label="Card Number")
          #card-element.input
        .cc-secondary
          b-field.cc-field.cc-expiry(for="card-expiry" label="Expiration Date")
            #card-expiry.input
          b-field.cc-field.cc-cvc(for="card-cvc" label="CVC Code")
            #card-cvc.input
      #card-errors.help.has-text-danger {{ stripeError }}
      .payment-total(v-if="event._embedded.packages.length == 1")
        p.is-size-5 Order Total
        h3.is-size-3 {{ amount | cents }}
  .form-controls
    button.button.is-medium.is-fullwidth.is-primary(
      type="submit"
      :class="{ 'is-loading': settings.submitting }"
    ) {{ submitLabel }}
</template>

<script>
/* global Stripe */
import _ from 'lodash';
import { mapGetters } from 'vuex';
import CcIcon from '@/components/CCIcon.vue';
import EventFormField from '@/components/events/EventFormField.vue';
import * as actionTypes from '@/store/types/actions';
import * as getterTypes from '@/store/types/getters';
import * as mutationTypes from '@/store/types/mutations';

export default {
  name: 'event-form',
  components: {
    CcIcon,
    EventFormField,
  },
  computed: {
    ...mapGetters({
      event: getterTypes.GET_EVENT,
      participants: getterTypes.GET_EVENT_REGISTRATION_PARTICIPANTS,
      registrationFields: getterTypes.GET_EVENT_REGISTRATION_FIELDS,
    }),
    amount() {
      if (!this.event.payment_required) return 0;
      return this.selectedPackage.cost * this.participants.length;
    },
    form() {
      if (this.event.id === undefined) return {};
      return this.event._embedded.form;
    },
    registrationPayload() {
      const payload = {
        id: this.event.id,
        amount: this.amount * 100,
        email: null,
        package_id: this.packageId,
        registrations: [],
      };
      let emailFields = [];
      _.filter(this.form.blocks, { type: 'custom' }).forEach((block) => {
        const f = _.filter(block.form_fields, { type: 'email' });
        emailFields = emailFields.concat(f);
      });
      if (emailFields.length > 0) {
        const [email] = emailFields.map((field) => {
          const hit = _.find(this.registrationFields, { form_field_id: field.id });
          if (hit) {
            return hit.value;
          }
          return null;
        });
        payload.email = email;
      }
      this.participants.forEach((participantFields) => {
        const obj = participantFields.concat(this.registrationFields);
        payload.registrations.push(obj);
      });
      return payload;
    },
    selectedPackage() {
      if (!this.packageId) return null;
      return _.find(this.event._embedded.packages, { id: this.packageId });
    },
    startingCost() {
      if (this.event._embedded.packages.length === 0) return 0;
      const costs = _.map(this.event._embedded.packages, 'cost');
      return _.min(costs);
    },
    submitLabel() {
      return this.event.payment_required ? 'Sign Up & Pay' : 'Sign Up';
    },
  },
  created() {
    if (this.event.payment_required && this.event._embedded.packages.length > 0) {
      this.packageId = this.event._embedded.packages[0].id;
      this.initStripe();
    }
  },
  data() {
    return {
      card: {
        number: null,
        expiry: null,
        cvc: null,
      },
      packageId: null,
      settings: {
        submitting: false,
      },
      stripe: Stripe(process.env.VUE_APP_STRIPE_API_KEY),
      stripeError: null,
    };
  },
  methods: {
    addParticipant() {
      this.$store.commit(mutationTypes.ADD_EVENT_REGISTRATION_PARTICIPANT, []);
    },
    async getStripeToken() {
      const { error, token } = await this.stripe.createToken(this.card.number);
      if (error) {
        this.stripeError = error.message;
        this.settings.submitting = false;
        return Promise.reject();
      }
      return Promise.resolve(token);
    },
    initStripe() {
      const elements = this.stripe.elements({
        fonts: [
          { cssSrc: 'https://fonts.googleapis.com/css?family=Lato' },
        ],
      });
      const style = {
        base: {
          color: '#363636',
          fontFamily: 'Lato',
          fontWeight: 400,
          fontSize: '16px',
          '::placeholder': {
            color: '#bcbcbc',
          },
        },
        invalid: {
          color: '#ff3860',
          iconColor: '#ff3860',
        },
      };
      this.card.number = elements.create('cardNumber', { style });
      this.card.expiry = elements.create('cardExpiry', { style });
      this.card.cvc = elements.create('cardCvc', { style });
    },
    async register(token) {
      const payload = this.registrationPayload;
      if (token) payload.token = token.id;
      try {
        await this.$store.dispatch(actionTypes.DO_CREATE_EVENT_REGISTRATION, payload);
        this.settings.submitting = false;
        this.$router.push({ name: 'registrationConfirmation', params: { id: this.event.key } });
      } catch (error) {
        this.$dialog.alert({
          title: 'Error',
          message: error.response.data.data.error_message,
          type: 'is-danger',
          hasIcon: true,
        });
        this.settings.submitting = false;
      }
    },
    removeParticipant(index) {
      if (index > 0) {
        this.$store.commit(mutationTypes.REMOVE_EVENT_REGISTRATION_PARTICIPANT, index);
      }
    },
    selectPackage(selected) {
      this.packageId = selected.id;
    },
    setField(value, field) {
      this.$store.commit(mutationTypes.SET_EVENT_REGISTRATION_FIELD, {
        form_field_id: field.id,
        label: field.label,
        value,
      });
    },
    setParticipantField(value, field, index) {
      this.$store.commit(mutationTypes.SET_EVENT_REGISTRATION_PARTICIPANT_FIELD, {
        form_field_id: field.id,
        label: field.label,
        value,
        index,
      });
    },
    async submit() {
      this.settings.submitting = true;
      this.stripeError = null;
      const valid = await this.$validator.validateAll();
      if (valid) {
        if (this.event.payment_required && this.amount > 0) {
          const token = await this.getStripeToken();
          await this.register(token);
        } else {
          await this.register();
        }
      } else {
        this.$dialog.alert({
          title: 'Error',
          message: 'Please fix the errors in the form',
          type: 'is-danger',
          hasIcon: true,
        });
        this.settings.submitting = false;
      }
      // const validateChildren = this.$children.map((child) => {
      //   const valid = await child.$validator.validateAll();
      //   return valid ? Promise.resolve(valid) : Promise.reject(valid);
      // });
      // try {
      //   const responses = await Promise.all(validateChildren);
      //   console.log(response);
      //
      // } catch (e) {
      //
      // }
    },
  },
  mounted() {
    if (this.event.payment_required) {
      this.card.number.mount('#card-element');
      this.card.expiry.mount('#card-expiry');
      this.card.cvc.mount('#card-cvc');
    }
  },
};
</script>

<style lang="scss">
#event-form {
  background-color: $white;
  padding: 1.5rem 1.5rem 3rem 1.5rem;
  .event-form-block {
    & + .event-form-block {
      margin-top: 3rem;
    }
    h3 {
      // margin-bottom: 0.15rem;
    }
    .event-form-block-paragraph {
      margin-bottom: 0.35rem;
    }
    .credit-cards {
      margin-bottom: 1.25rem;
      .cc-icon + .cc-icon {
        margin-left: 0.35rem;
      }
    }
    .event-packages {
      .package-heading {
        margin-bottom: 0.75rem;
      }
    }
    .payment-total {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      padding: 0.5rem 0;
      margin: 1rem 0;
      border-top: 1px solid $white-ter;
      border-bottom: 1px solid $white-ter;
      p {
        text-transform: uppercase;
        margin-right: 1.5rem;
        font-family: $heading-font;
      }
    }
    .payment-fields {
      margin-bottom: 0 !important;
      & > * {
        margin-bottom: 0;
      }
      .cc-field {
        flex-grow: 1;
        &.cc-number {
          flex-grow: 4;
        }
      }
      .cc-secondary {
        display: flex;
        flex-grow: 2;
      }
      .input {
        font-weight: 400 !important;
        &.StripeElement--invalid {
          background-color: #ffebeb !important;
          border-color: #ff3860 !important;
        }
        & > div {
          width: 100%;
        }
      }
    }
  }
  .form-controls {
    display: flex;
    justify-content: center;
    margin-top: 2.5rem;
    .button {
      font-family: $heading-font;
    }
  }
}

@media (max-width: $tablet) {
  #event-form {
    .participant {
      border: 1px solid $grey-lighter;
      border-radius: 4px;
      padding: 1rem;
      position: relative;
      .participant-actions {
        position: absolute;
        top: -24px;
        right: -8px;
        margin-top: 0 !important;
      }
      .field.is-grouped {
        flex-flow: column;
      }
      .field + .field {
        margin-top: 1.25rem;
      }
    }
    .field.is-grouped {
      .field {
        &:last-child {
          margin-right: 0 !important;
          margin-bottom: 0;
        }
      }
    }
    .address-field {
      .field:first-child {
        margin-right: 0;
      }
    }
    .payment-fields {
      flex-flow: column;
      .cc-number {
        margin-right: 0;
      }
      .cc-secondary {
        margin-top: 1.25rem;
      }
    }
  }
}
</style>
